import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { ApplicationContextProvider } from "./context/GlobalState";
import App from "./App";
import "./index.css";

// First-load render-jump fix, part 2 (2026-07-17; part 1 = home hero fallback,
// 2026-07-10). scripts/prerender-schema.js injects a static SEO body inside
// #root; createRoot().render() clobbers it while the lazy route chunk is still
// downloading, so the old Suspense fallback (an empty 100vh div) produced a
// visible content → BLANK → content flash on every prerendered content page.
// Capture the static body BEFORE React mounts; the route-level Suspense
// fallback re-paints it verbatim until the real page chunk arrives.
// (The sibling data-prerender-header spacer is intentionally dropped — the
// real <Header> renders immediately and occupies the same 72px slot.)
try {
  const staticBody = document.querySelector('#root [data-prerender-body="1"]');
  if (staticBody) window.__PRERENDER_BODY_HTML__ = staticBody.outerHTML;
} catch (e) { /* non-fatal: fall back to the blank div */ }

// First-load render-jump fix, part 3 (2026-07-21): postbuild-brand-og.js makes
// main.css NON-render-blocking on the HOME shells (media="print" -> onload
// media='all') so the inline static hero paints instantly. But a non-matching
// media stylesheet does NOT block deferred script execution, so when main.js
// is cached React could hydrate the full app BEFORE main.css applied — one
// unstyled frame (mobile sidebar visible, then transition slides it away).
// Gate the mount on the deferred stylesheet actually being applied; the
// inline-styled static hero shell stays on screen meanwhile, so LCP/FCP are
// unaffected. Route clones keep a render-blocking link and resolve instantly.
function whenMainCssApplied() {
  const link = document.querySelector('link[rel="stylesheet"][href*="/static/css/main."]');
  // No deferred link (route clones / dev server) or already flipped to 'all'.
  if (!link || !link.media || link.media === "all") return Promise.resolve();
  return new Promise((resolve) => {
    let settled = false;
    const done = () => {
      if (settled) return;
      settled = true;
      // Ensure styles are applicable even if the onload attribute was lost.
      try { link.media = "all"; } catch (e) { /* no-op */ }
      resolve();
    };
    link.addEventListener("load", done, { once: true });
    link.addEventListener("error", done, { once: true });
    // Safety valve: never hold the app hostage to a hung stylesheet request.
    setTimeout(done, 4000);
  });
}

function mount() {
  // 2026-07-21 audit M2: prerender-schema marks its static head mirrors
  // (canonical/description/og/twitter/hreflang) with data-pr="1". Remove them
  // before React mounts so Helmet's managed copies don't end up duplicated
  // next to the static ones (two canonicals/two og:titles in the live DOM).
  // Crawlers that don't run JS still see the static set in the raw HTML.
  try {
    document.querySelectorAll('[data-pr="1"]').forEach((el) => el.remove());
  } catch (e) { /* non-fatal */ }
  const root = ReactDOM.createRoot(document.getElementById("root"));
  root.render(
    <React.StrictMode>
      <HelmetProvider>
        <ApplicationContextProvider>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </ApplicationContextProvider>
      </HelmetProvider>
    </React.StrictMode>
  );
}

whenMainCssApplied().then(mount);
